import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { MealPlan, Property } from 'api/types';
import { describeMeals, toMainMeal } from 'utils/mealPlanMeals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface MealPlanViewModalProps {
  isOpen: boolean;
  mealPlan: MealPlan | null;
  propertyLookup: Record<string, Property>;
  onClose: () => void;
  onEdit?: (mealPlan: MealPlan) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (id: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// COMPONENT
// ============================================================================

const MealPlanViewModal: React.FC<MealPlanViewModalProps> = ({
  isOpen,
  mealPlan,
  propertyLookup,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !mealPlan) return;
    minimize({
      id: `${tabId}-view-modal-${mealPlan.id}`,
      title: `View - ${mealPlan.planCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(mealPlan.id as string),
    });
    onClose();
  }, [tabId, moduleName, mealPlan, minimize, onClose, onRestoreView]);

  if (!isOpen || !mealPlan) return null;

  // Resolve FK display names from lookups
  const property = mealPlan.propertyId ? propertyLookup[mealPlan.propertyId] : null;
  const propertyDisplay = property ? `${property.propertyCode} - ${property.propertyName}` : '-';

  // Format rates
  const formatRate = (rate?: number) => rate != null ? rate.toFixed(2) : '-';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Meal Plan Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Basic Information */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Plan Code" value={mealPlan.planCode} />
            <ReadOnlyField label="Plan Name" value={mealPlan.planName} />
          </div>

          {/* Property */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Property" value={propertyDisplay} />
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${mealPlan.isActive ? 'active' : 'inactive'}`}>
                  {mealPlan.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          {mealPlan.description && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="Description" value={mealPlan.description} />
            </div>
          )}

          {/* Meal Inclusions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Meals Included" value={describeMeals(mealPlan)} />
          </div>

          {/* MAP: which of lunch or dinner counts as the day's plan meal. */}
          {toMainMeal(mealPlan) === 'ONE_OF' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField
                label="Which Meal Counts"
                value={
                  mealPlan.majorMealRule === 'FIXED'
                    ? `Only ${mealPlan.defaultMajorMeal === 'LUNCH' ? 'lunch' : 'dinner'}, unless Front Office changes the day`
                    : "Guest's choice — lunch or dinner"
                }
              />
            </div>
          )}

          {/* Rates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Default Adult Rate" value={formatRate(mealPlan.adultRate)} />
            <ReadOnlyField label="Default Child Rate" value={formatRate(mealPlan.childRate)} />
            <ReadOnlyField label="Tax Inclusive" value={mealPlan.includeTaxes} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(mealPlan);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default MealPlanViewModal;
